import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { PREVIEW_LEADER_USER_ID } from "@/preview/leaderPreviewContext";

export interface LeaderOnboardingState {
  coach_validated: boolean;
  hasBehaviors: boolean;
  hasOnboarding: boolean;
}

export function getLeaderOnboardingStateQueryKey(leaderId?: string) {
  return ["leader-onboarding-state", leaderId] as const;
}

export async function fetchLeaderOnboardingState(
  leaderId?: string | null,
): Promise<LeaderOnboardingState | null> {
  if (!leaderId) return null;
  if (leaderId === PREVIEW_LEADER_USER_ID) {
    return {
      coach_validated: true,
      hasBehaviors: true,
      hasOnboarding: true,
    };
  }

  const [{ data: onboarding, error: onboardingError }, { count, error: behaviorsError }] =
    await Promise.all([
      supabase
        .from("leader_onboarding")
        .select("coach_validated")
        .eq("leader_id", leaderId)
        .maybeSingle(),
      supabase
        .from("leader_behaviors")
        .select("id", { count: "exact", head: true })
        .eq("leader_id", leaderId),
    ]);

  if (onboardingError) {
    console.error("Error fetching leader onboarding:", onboardingError);
    throw onboardingError;
  }

  if (behaviorsError) {
    console.error("Error fetching leader behaviors:", behaviorsError);
    throw behaviorsError;
  }

  return {
    coach_validated: onboarding?.coach_validated ?? false,
    hasBehaviors: (count ?? 0) > 0,
    hasOnboarding: !!onboarding,
  };
}

export function useLeaderOnboardingState(
  leaderId?: string | null,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: getLeaderOnboardingStateQueryKey(leaderId ?? undefined),
    queryFn: () => fetchLeaderOnboardingState(leaderId),
    enabled: (options?.enabled ?? true) && Boolean(leaderId),
  });
}
